// Define the shape expected by Gemini (data = base64 string)
export interface GeminiAudioBlob {
  data: string;
  mimeType: string;
}

// Robust Downsampling from Any Rate -> 16000Hz
function downsampleTo16k(input: Float32Array, inputRate: number): Float32Array {
  if (inputRate === 16000) return input;
  
  const ratio = inputRate / 16000;
  const newLength = Math.ceil(input.length / ratio);
  const result = new Float32Array(newLength);
  
  for (let i = 0; i < newLength; i++) {
    const offset = Math.floor(i * ratio);
    // Boundary check
    if (offset < input.length) {
        result[i] = input[offset];
    } else {
        result[i] = 0; // Pad with silence if calculation goes slightly over
    }
  }
  return result;
}

// Helper to convert raw Float32Array from AudioContext to Gemini-compatible PCM Blob
export function createBlob(data: Float32Array, inputSampleRate: number): GeminiAudioBlob {
  // 1. Downsample if necessary (e.g. 48k -> 16k)
  const processedData = downsampleTo16k(data, inputSampleRate);
  
  // 2. Convert to Int16
  const l = processedData.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    // Clamp values to [-1, 1] before converting to PCM16
    const s = Math.max(-1, Math.min(1, processedData[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }

  // 3. Return Base64 encoded PCM
  // We manually iterate to avoid call stack limits with spread syntax on large arrays
  let binary = '';
  const bytes = new Uint8Array(int16.buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return {
    data: btoa(binary),
    mimeType: 'audio/pcm;rate=16000',
  };
}